import "./UseCaseDashboardPage.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { H2, H3, Text } from "@amzn/stencil-react-components/text";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { UseCaseCard } from "./components/UseCaseCard";
import { DashboardTab, UseCaseDashboardTouchpoint } from "./constants";
import { useContributorDashboard } from "./hooks/useContributorDashboard";
import { useToolNames } from "./hooks/useToolNames";
import { useUseCaseMetadata } from "./hooks/useUseCaseMetadata";

const STATS_KEYS = [
  { labelKey: "useCases.dashboard.statsContributed", field: "totalContributed" },
  { labelKey: "useCases.dashboard.statsViews", field: "totalViews" },
  { labelKey: "useCases.dashboard.statsUsedClicks", field: "totalUsedClicks" },
  { labelKey: "useCases.dashboard.statsComments", field: "totalComments" },
] as const;

const UseCaseDashboardPage: FC = () => {
  const history = useHistory();
  const { useCases: fetchedUseCases, stats: fetchedStats, isLoading } = useContributorDashboard();
  const { labelMap } = useUseCaseMetadata();

  const useCases = fetchedUseCases;
  const stats = fetchedStats;

  const allToolIds = useMemo(() => {
    const ids = new Set<string>();
    useCases.forEach((uc) => uc.associatedToolIds?.forEach((id) => ids.add(id)));
    return Array.from(ids);
  }, [useCases]);
  const toolNames = useToolNames(allToolIds);

  const tabs = useMemo(
    () => [
      {
        value: DashboardTab.UseCases,
        label: `${getString("useCases.dashboard.tabUseCases")} (${stats.totalContributed})`,
      },
    ],
    [stats.totalContributed]
  );
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  if (isLoading) {
    return (
      <Col alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner size={SpinnerSize.Small} />
        <Text fontSize="S100">{getString("useCases.dashboard.loading")}</Text>
      </Col>
    );
  }

  return (
    <div className="use-case-dashboard">
      <div className="use-case-dashboard__hero">
        <PageMainContainer>
          <Breadcrumbs
            crumbs={[
              { title: getString("logoText"), url: "/" },
              { title: getString("aiForMe.title"), url: "/aiforme" },
              { title: getString("useCases.browsePage.breadcrumbUseCases"), url: "/aiforme/usecases" },
              { title: getString("useCases.dashboard.breadcrumbDashboard") },
            ]}
            onCrumbClick={(e, crumb: CrumbProps) => {
              if (crumb.url) {
                e.preventDefault();
                history.push(crumb.url);
              }
            }}
          />

          <Row justifyContent="space-between" alignItems="flex-start" className="use-case-dashboard__header">
            <Col gridGap="S100">
              <H2>{getString("useCases.dashboard.title")}</H2>
              <Text fontSize="T200" color="var(--st-dt-color-text-secondary)">
                {getString("useCases.dashboard.subtitle")}
              </Text>
            </Col>
            <Row gridGap="S200">
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => history.push(Paths.aiForMeUseCases.url)}
                touchpoint={UseCaseDashboardTouchpoint.BrowseUseCases}
              >
                {getString("useCases.dashboard.browseUseCases")}
              </Button>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => history.push(Paths.aiForMeUseCaseContribute.url)}
                touchpoint={UseCaseDashboardTouchpoint.ExploreTools}
              >
                {getString("useCases.dashboard.exploreTools")}
              </Button>
            </Row>
          </Row>
        </PageMainContainer>
      </div>

      <PageMainContainer>
        <Col gridGap="S400" className="use-case-dashboard__content">
          <Row gridGap="S400" alignItems="center">
            <TabBar {...tabBarProps} />
            <Button
              variant={ButtonVariant.Tertiary}
              onClick={() => history.push(Paths.aiformeMyTools.url)}
              touchpoint={UseCaseDashboardTouchpoint.TabAiTools}
            >
              <Row gridGap="S100" alignItems="center">
                <Text>{getString("useCases.dashboard.tabAiTools")}</Text>
                <IconExternalLinkSmall aria-hidden />
              </Row>
            </Button>
          </Row>
          <TabSwitcher {...tabSwitcherProps}>
            <TabPanel value={DashboardTab.UseCases}>
              <Col gridGap="S400">
                <Row gridGap="S300" className="use-case-dashboard__stats">
                  {STATS_KEYS.map(({ labelKey, field }) => (
                    <Card key={field} className="use-case-dashboard__stat-card">
                      <Col gridGap="S100">
                        <Text fontSize="T500" fontWeight="bold">
                          {stats[field]}
                        </Text>
                        <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                          {getString(labelKey)}
                        </Text>
                      </Col>
                    </Card>
                  ))}
                </Row>

                <Row justifyContent="space-between" alignItems="center">
                  <H3>{getString("useCases.dashboard.myUseCasesTitle")}</H3>
                  <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                    {getString("useCases.dashboard.contributedCount", { count: String(stats.totalContributed) })}
                  </Text>
                </Row>

                {useCases.length === 0 ? (
                  <Col alignItems="center" padding="S500" gridGap="S300">
                    <Text>{getString("useCases.dashboard.noUseCases")}</Text>
                    <Button
                      variant={ButtonVariant.Primary}
                      onClick={() => history.push(Paths.aiForMeUseCaseContribute.url)}
                      touchpoint={UseCaseDashboardTouchpoint.StartContributing}
                    >
                      {getString("useCases.dashboard.startContributing")}
                    </Button>
                  </Col>
                ) : (
                  <div className="use-case-dashboard__grid">
                    {useCases.map((useCase) => (
                      <UseCaseCard
                        key={useCase.taxonomyId}
                        useCase={useCase}
                        toolNames={toolNames}
                        labelMap={labelMap}
                        showBookmark={false}
                      />
                    ))}
                  </div>
                )}
              </Col>
            </TabPanel>
          </TabSwitcher>
        </Col>
      </PageMainContainer>
    </div>
  );
};

export default UseCaseDashboardPage;
